import React, { useState, useEffect } from "react";
import { createPageUrl } from "@/utils";
import { db } from "@/api/db";
import { CheckCircle, XCircle, Clock, ArrowLeft } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Link } from "react-router-dom";

export default function ExamPage() {
  const urlParams = new URLSearchParams(window.location.search);
  const examId = urlParams.get("id");

  const [exam, setExam] = useState(null);
  const [user, setUser] = useState(null);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [previousAttempt, setPreviousAttempt] = useState(null);

  useEffect(() => {
    if (examId) loadData();
  }, [examId]);

  async function loadData() {
    const u = await db.auth.me();
    setUser(u);
    const [examList, attempts] = await Promise.all([
      db.entities.Exam.filter({ id: examId }),
      db.entities.ExamAttempt.filter({ user_email: u.email, exam_id: examId }, "-created_date", 1),
    ]);
    setExam(examList[0] || null);
    setPreviousAttempt(attempts[0] || null);
    setLoading(false);
  }

  async function handleSubmit() {
    const questions = exam.questions || [];
    let correct = 0;
    questions.forEach((q, i) => {
      if (answers[i] === q.correct_index) correct++;
    });
    const score = questions.length > 0 ? Math.round((correct / questions.length) * 100) : 0;
    const passed = score >= (exam.passing_score || 70);
    const pts = passed ? (exam.points || 50) : 0;

    await db.entities.ExamAttempt.create({
      user_email: user.email,
      exam_id: exam.id,
      level_id: exam.level_id,
      track_id: exam.track_id,
      score,
      passed,
      answers: Object.values(answers),
      points_earned: pts,
      completed_at: new Date().toISOString(),
    });

    if (passed) {
      const profiles = await db.entities.UserProfile.filter({ user_email: user.email }, "-created_date", 1);
      if (profiles[0]) {
        await db.entities.UserProfile.update(profiles[0].id, {
          portus_score: (profiles[0].portus_score || 0) + pts,
        });
      }

      const existing = await db.entities.Certificate.filter({
        user_email: user.email, level_id: exam.level_id
      });
      if (existing.length === 0) {
        await db.entities.Certificate.create({
          user_email: user.email,
          user_name: user.full_name,
          track_id: exam.track_id,
          level_id: exam.level_id,
          track_title: exam.title,
          level_title: exam.title,
          issued_at: new Date().toISOString(),
          certificate_code: `PORTUS-${Date.now()}`,
        });
      }
    }

    setResult({ score, passed, correct, total: questions.length, pts });
    setSubmitted(true);
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin"
          style={{ borderColor: '#00DCA4', borderTopColor: 'transparent' }} />
      </div>
    );
  }

  if (!exam) return (
    <div className="text-center py-16" style={{ color: '#99A6AB' }}>Prova não encontrada.</div>
  );

  const questions = exam.questions || [];

  // Result screen
  if (submitted && result) {
    return (
      <div className="max-w-2xl mx-auto space-y-6">
        <Card className="border-0 shadow-md" style={{ background: result.passed ? '#EAFAF6' : '#FADBDF' }}>
          <CardContent className="p-8 text-center">
            {result.passed ? (
              <CheckCircle className="w-16 h-16 mx-auto mb-4" style={{ color: '#00DCA4' }} />
            ) : (
              <XCircle className="w-16 h-16 mx-auto mb-4" style={{ color: '#892E38' }} />
            )}
            <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Poppins, sans-serif', color: '#003326' }}>
              {result.passed ? "Parabéns! Aprovado! 🎉" : "Não aprovado desta vez"}
            </h2>
            <p className="text-4xl font-bold mb-1" style={{ fontFamily: 'Poppins, sans-serif', color: '#003326' }}>
              {result.score}%
            </p>
            <p className="mb-2" style={{ color: '#646C6F' }}>
              {result.correct} de {result.total} questões corretas
            </p>
            {result.passed && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium mt-2"
                style={{ background: '#B0FCEA', color: '#003326' }}>
                +{result.pts} Portus Score
              </span>
            )}
            {result.passed && (
              <p className="text-sm mt-3" style={{ color: '#00664C' }}>Certificado emitido automaticamente!</p>
            )}
          </CardContent>
        </Card>

        <div className="flex gap-3">
          <Link to={createPageUrl("Tracks")} className="flex-1">
            <button className="w-full py-3 rounded-full text-sm font-semibold flex items-center justify-center gap-2"
              style={{ background: '#F2F4F4', color: '#646C6F' }}>
              <ArrowLeft className="w-4 h-4" /> Ver trilhas
            </button>
          </Link>
          {!result.passed && (
            <button
              onClick={() => { setSubmitted(false); setAnswers({}); setResult(null); }}
              className="flex-1 py-3 rounded-full text-sm font-bold"
              style={{ background: '#00DCA4', color: '#003326' }}>
              Tentar novamente
            </button>
          )}
        </div>
      </div>
    );
  }

  // Previous passed attempt
  if (previousAttempt?.passed) {
    return (
      <div className="max-w-2xl mx-auto text-center py-16">
        <CheckCircle className="w-12 h-12 mx-auto mb-4" style={{ color: '#00DCA4' }} />
        <h2 className="text-xl font-bold mb-2" style={{ fontFamily: 'Poppins, sans-serif', color: '#003326' }}>
          Você já passou nesta prova!
        </h2>
        <p className="mb-6" style={{ color: '#646C6F' }}>Nota: {previousAttempt.score}%</p>
      </div>
    );
  }

  const answeredCount = Object.keys(answers).length;

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold" style={{ fontFamily: 'Poppins, sans-serif', color: '#2E3233' }}>
          {exam.title}
        </h1>
        <div className="flex items-center gap-4 mt-2 text-sm" style={{ color: '#646C6F' }}>
          <span>{questions.length} questões</span>
          <span>Nota mínima: {exam.passing_score || 70}%</span>
          {exam.time_limit_minutes && (
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />{exam.time_limit_minutes} min
            </span>
          )}
        </div>
        <Progress value={(answeredCount / questions.length) * 100} className="mt-3 h-2" />
        <p className="text-xs mt-1" style={{ color: '#99A6AB' }}>{answeredCount}/{questions.length} respondidas</p>
      </div>

      {questions.map((q, qi) => (
        <Card key={qi} className="border-0 shadow-sm">
          <CardContent className="p-5">
            <p className="font-medium mb-4" style={{ color: '#2E3233' }}>{qi + 1}. {q.question}</p>
            <div className="space-y-2">
              {(q.options || []).map((opt, oi) => (
                <button
                  key={oi}
                  onClick={() => setAnswers(prev => ({ ...prev, [qi]: oi }))}
                  className="w-full text-left px-4 py-3 rounded-xl text-sm font-medium transition-all"
                  style={answers[qi] === oi
                    ? { border: '2px solid #00DCA4', background: '#EAFAF6', color: '#003326' }
                    : { border: '2px solid #E5E9EA', color: '#2E3233', background: '#fff' }}
                  onMouseEnter={e => { if (answers[qi] !== oi) e.currentTarget.style.borderColor = '#00DCA4'; }}
                  onMouseLeave={e => { if (answers[qi] !== oi) e.currentTarget.style.borderColor = '#E5E9EA'; }}
                >
                  {opt}
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      ))}

      {questions.length === 0 && (
        <div className="text-center py-12" style={{ color: '#99A6AB' }}>Esta prova ainda não tem questões.</div>
      )}

      {questions.length > 0 && (
        <button
          onClick={handleSubmit}
          disabled={answeredCount < questions.length}
          className="w-full py-4 rounded-full text-base font-bold transition-opacity"
          style={answeredCount >= questions.length
            ? { background: '#00DCA4', color: '#003326' }
            : { background: '#E5E9EA', color: '#99A6AB' }}
        >
          Enviar respostas ({answeredCount}/{questions.length})
        </button>
      )}
    </div>
  );
}
